import {
  create,
  fromJson,
  toJson,
  type DescMessage,
  type JsonValue,
  type MessageShape,
} from '@bufbuild/protobuf';
import { apiClient } from './request';
import { GetRequestSchema, ListRequestSchema } from '@/proto/agentplatform/common/http_pb';
import {
  CreateSecretRequestSchema,
  CreateSecretResponseSchema,
  DeleteSecretResponseSchema,
  GetSecretResponseSchema,
  ListSecretsResponseSchema,
  UpdateSecretRequestSchema,
  UpdateSecretResponseSchema,
  ValidateOSSSecretRequestSchema,
  ValidateOSSSecretResponseSchema,
  type ListSecretsData,
} from '@/proto/agentplatform/httpv2/secret_pb';
import type { Secret, GenericServiceSecretData } from '@/proto/agentplatform/v2/secret_pb';
import type { ListRequest } from '@/proto/agentplatform/common/http_pb';

/**
 * 拆 httpv2 成功包络取业务 data。envelope 的业务错误已由 apiClient 响应拦截器
 * 统一识别并 reject 成 ApiError（见 request.ts / errors.ts），这里只处理成功分支。
 */
async function send<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  const wrapped = fromJson(successSchema, responseObject.data);
  const data = pickData(wrapped);
  if (data === undefined || data === null) {
    throw new Error('empty response data');
  }
  return data;
}

/** 空 envelope 的成功响应（Update/Delete 无需回读 data）；只做格式校验。 */
async function sendVoid<TResponse extends DescMessage>(
  path: string,
  body: JsonValue,
  successSchema: TResponse
): Promise<void> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  fromJson(successSchema, responseObject.data);
}

export const secretApi = {
  listSecrets: async (params: ListRequest): Promise<ListSecretsData> =>
    send(
      '/v2/ListSecrets',
      toJson(ListRequestSchema, params),
      ListSecretsResponseSchema,
      (r) => r.data
    ),

  getSecret: async (id: string): Promise<Secret> =>
    send(
      '/v2/GetSecret',
      toJson(GetRequestSchema, create(GetRequestSchema, { id })),
      GetSecretResponseSchema,
      (r) => r.data
    ),

  createSecret: async (secret: Secret): Promise<void> =>
    sendVoid(
      '/v2/CreateSecret',
      toJson(CreateSecretRequestSchema, create(CreateSecretRequestSchema, { secret })),
      CreateSecretResponseSchema
    ),

  /** 内联挂载场景：创建凭证并返回其 id，用于填入 MountPoint.secretId。 */
  createSecretReturningId: async (secret: Secret): Promise<string> =>
    send(
      '/v2/CreateSecret',
      toJson(CreateSecretRequestSchema, create(CreateSecretRequestSchema, { secret })),
      CreateSecretResponseSchema,
      (r) => r.data?.id
    ),

  updateSecret: async (id: string, secret: Secret): Promise<void> =>
    sendVoid(
      '/v2/UpdateSecret',
      toJson(UpdateSecretRequestSchema, create(UpdateSecretRequestSchema, { id, secret })),
      UpdateSecretResponseSchema
    ),

  deleteSecret: async (id: string): Promise<void> =>
    sendVoid(
      '/v2/DeleteSecret',
      toJson(GetRequestSchema, create(GetRequestSchema, { id })),
      DeleteSecretResponseSchema
    ),

  /**
   * 联网预校验对象存储凭证对指定 bucket 的可达性。凭证来源二选一：已有凭证传
   * secretId；新建凭证传明文 data（oneof 分支即类型）。失败时 apiClient 拦截器
   * 会 reject 成 ApiError。
   */
  validateOSSSecret: async (input: ValidateOSSSecretInput): Promise<void> => {
    const req = create(
      ValidateOSSSecretRequestSchema,
      'secretId' in input
        ? { buckets: input.buckets, secretId: input.secretId }
        : {
            buckets: input.buckets,
            data: { case: input.storageType, value: input.body },
          }
    );
    return sendVoid(
      '/v2/ValidateOSSSecret',
      toJson(ValidateOSSSecretRequestSchema, req),
      ValidateOSSSecretResponseSchema
    );
  },
};

/** 校验已有凭证：只需 secretId + 待验证 buckets。 */
export interface ValidateExistingSecretInput {
  buckets: string[];
  secretId: string;
}

/** 校验新建凭证：明文 data（oneof 分支即类型）+ 待验证 buckets。 */
export interface ValidateInlineSecretInput {
  buckets: string[];
  storageType: 'aliyunOss' | 'volcengineTos';
  body: GenericServiceSecretData;
}

export type ValidateOSSSecretInput = ValidateExistingSecretInput | ValidateInlineSecretInput;
